<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import LoadingElement from "@/components/Animations/LoadingElement.vue";
import TopDeck from "@/components/Utils/TopDeck.vue";
import { useAuthStore } from "@/store/auth_store";
import {
  getUserIngredients,
  deleteIngredientFromUserCollection,
  setIngredientAsFavorite,
  updateProduct,
} from "@/service/ingredient_service";
import EditIngredientModal from './EditIngredientModal.vue'
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import AccountToggle from "@/components/Utils/AccountToggle.vue";

export default {
  name: "MyIngredients",
  components: { LoadingElement, TopDeck, ThemeToggle, AccountToggle, EditIngredientModal },
  setup() {
    const authStore = useAuthStore();
    const errorMessage = ref("");
    const snackbar = ref({ show: false, text: "", color: "success" });
    const router = useRouter();

    const ingredients = ref([]);
    const isLoadingIngredients = ref(false);
    const selectedIngredient = ref(null);
    const searchQuery = ref("");

    const selectedCategory = ref("Tous");
    const editModalOpen = ref(false);
    
    // Modal de confirmation
    const deleteDialog = ref(false);
    const ingredientToDelete = ref(null);

    // Filtres de tri
    const sortBy = ref(null);
    const sortMenuOpen = ref(false);

    const categories = computed(() => {
      const uniqueCategories = new Set(
        ingredients.value
          .map((item) => (item.category ?? "").trim())
          .filter((cat) => cat.length > 0)
      );

      return ["Tous", ...Array.from(uniqueCategories).sort()];
    });

    const filteredIngredients = computed(() => {
      const q = searchQuery.value.trim().toLowerCase();

      let filtered = ingredients.value.filter((item) => {
        const matchesSearch = ["productName", "productBrand", "category"].some(
          (key) => {
            return (item[key] ?? "").toLowerCase().includes(q);
          }
        );

        const matchesCategory =
          selectedCategory.value === "Tous" ||
          (item.category ?? "").toLowerCase() ===
            selectedCategory.value.toLowerCase();

        return matchesSearch && matchesCategory;
      });

      // Appliquer le tri
      if (sortBy.value) {
        filtered = [...filtered].sort((a, b) => {
          switch (sortBy.value) {
            case 'name-asc':
              return (a.productName || '').localeCompare(b.productName || '');
            case 'name-desc':
              return (b.productName || '').localeCompare(a.productName || '');
            case 'brand-asc':
              return (a.productBrand || '').localeCompare(b.productBrand || '');
            case 'brand-desc':
              return (b.productBrand || '').localeCompare(a.productBrand || '');
            case 'category-asc':
              return (a.category || '').localeCompare(b.category || '');
            case 'category-desc':
              return (b.category || '').localeCompare(a.category || '');
            case 'portion-asc':
              return (a.servingQuantity || 0) - (b.servingQuantity || 0);
            case 'portion-desc':
              return (b.servingQuantity || 0) - (a.servingQuantity || 0);
            case 'favorite': {
              const aFav = isItemFavorite(a) ? 1 : 0;
              const bFav = isItemFavorite(b) ? 1 : 0;
              return bFav - aFav;
            }
            default:
              return 0;
          }
        });
      }

      return filtered;
    });

    function select(item) {
      selectedIngredient.value = item;
    }

    function isItemFavorite(item) {
      if(item.isBaseIngredient) {
        return authStore.userInfos?.favoriteBaseIngredients?.includes(item.id);
      } else {
        return item.favorite;
      }
    }

    function confirmDelete(item = null) {
      // Si item est fourni (depuis le tableau), utiliser celui-ci
      // Sinon utiliser selectedIngredient (depuis le panneau latéral)
      const target = item || selectedIngredient.value;
      if (!target) return;
      
      ingredientToDelete.value = target;
      deleteDialog.value = true;
    }

    async function deleteSelectedIngredient() {
      if (!ingredientToDelete.value || !authStore.userid) {
        deleteDialog.value = false;
        return;
      }

      try {
        const result = await deleteIngredientFromUserCollection(
          authStore.userid,
          ingredientToDelete.value.id
        );

        // Supprimer de la liste locale
        ingredients.value = ingredients.value.filter(
          (item) => item.id !== ingredientToDelete.value.id
        );

        // Si l'ingrédient supprimé était sélectionné, le désélectionner
        if (selectedIngredient.value?.id === ingredientToDelete.value.id) {
          selectedIngredient.value = null;
        }

        // Message de succès
        let message = "Ingrédient supprimé avec succès.";
        if (result.deletedRecipes > 0) {
          message += ` ${result.deletedRecipes} recette(s) contenant cet ingrédient ont également été supprimée(s).`;
        }

        snackbar.value = { 
          show: true, 
          text: message, 
          color: "success" 
        };

        ingredientToDelete.value = null;
      } catch (err) {
        console.error("❌ Erreur suppression :", err);
        errorMessage.value = "Erreur lors de la suppression de l'ingrédient.";
        snackbar.value = { 
          show: true, 
          text: errorMessage.value, 
          color: "error" 
        };
      } finally {
        deleteDialog.value = false;
      }
    }

    async function toggleFavorite(selectedIngredient) {
      if (!selectedIngredient || !authStore.userid) return;

      try {
        const id = selectedIngredient.id;
        const current = ingredients.value.find((i) => i.id === id);
        if (!current) return;

        const newValue = !current.favorite;

        await setIngredientAsFavorite(authStore.userid, id, newValue);
        
        current.favorite = newValue;
        selectedIngredient.favorite = newValue;
      } catch (err) {
        console.error("❌ Erreur favoris :", err);
        errorMessage.value = "Erreur lors de la mise à jour du favori.";
        snackbar.value = { show: true, text: errorMessage.value, color: "error" };
      }
    }

    async function onIngredientSaved(updated) {
      if (!updated || !authStore.userid) return;

      try {
        const id = updated.id;
        const payload = { ...updated };
        delete payload.source;

        await updateProduct(authStore.userid, { [id]: payload });

        const idx = ingredients.value.findIndex((i) => i.id === id);
        if (idx !== -1) {
          ingredients.value[idx] = { ...ingredients.value[idx], ...payload };
        }
        selectedIngredient.value = { ...selectedIngredient.value, ...payload };
        
        snackbar.value = { show: true, text: "Ingrédient modifié avec succès.", color: "success" };
      } catch (err) {
        console.error('Erreur sauvegarde ingrédient :', err);
        errorMessage.value = 'Erreur lors de la sauvegarde de l\'ingrédient.';
        snackbar.value = { show: true, text: errorMessage.value, color: "error" };
      }
    }

    onMounted(() => {
      watch(
        () => authStore.userInfos,
        async () => {
          if (!authStore?.userid) return;
          try {
            isLoadingIngredients.value = true;

            const userData = await getUserIngredients(authStore.userid);

            ingredients.value = Object.entries(userData).map(([id, data]) => ({
              id,
              ...data,
              source: "user",
            }));

          } catch (error) {
            console.error("Erreur récupération ingrédients:", error);
            errorMessage.value = "Erreur lors du chargement des ingrédients.";
          } finally {
            isLoadingIngredients.value = false;
          }

        },
        { immediate: true }
      );
    });

    return {
      selectedCategory,
      categories,
      authStore,
      errorMessage,
      snackbar,
      router,
      ingredients,
      selectedIngredient,
      select,
      searchQuery,
      filteredIngredients,
      isLoadingIngredients,
      confirmDelete,
      deleteSelectedIngredient,
      toggleFavorite,
      isItemFavorite,
      editModalOpen,
      onIngredientSaved,
      deleteDialog,
      ingredientToDelete,
      sortBy,
      sortMenuOpen,
    };
  },
};
</script>

<template>
  <loading-element v-if="authStore.isLoading" />
  <div class="main bg-primary" v-else>
    <div class="rowSpaceBetween fullWidth mb-5">
      <TopDeck
        v-if="authStore.userInfos"
        firstElement="Mes"
        secondElement="ingrédients"
        :coloredSecond="true"
      />
      <div class="rowCustom">
        <ThemeToggle />
        <AccountToggle />
      </div>
    </div>

    <v-row class="mt-6" no-gutters>
      <!-- Liste d'ingrédients -->
      <v-col cols="12" md="9" class="pr-2">

        <div
          class="d-flex flex-column align-start justify-center pa-4 browseBaseRecipesBanner rounded-xl"
        >
          <h2 class="font-weight-medium mb-1">Pour bien commencer</h2>
          <p class="mb-2">
            Parcourez une sélection d'ingrédients de base à ajouter à votre collection, comme des épices ou des produits frais.
          </p>
          <v-btn
            color="white"
            prepend-icon="mdi-food-apple"
            elevation="0"
            rounded="lg"
            size="small"
            @click="router.push('/browse_base_ingredients')"
          >
            Parcourir les ingrédients de base
          </v-btn>
        </div>

        <div class="pa-4 bg-secondary rounded-xl">
          <div class="headerWithAction">
            <h2 class="text">Votre liste</h2>
            <v-btn
              color="white"
              prepend-icon="mdi-plus"
              elevation="0"
              rounded="lg"
              size="small"
              @click="router.push('/add_ingredient')"
            >
              Ajouter ingrédient
            </v-btn>
          </div>

          <v-slide-group v-model="selectedCategory" show-arrows class="mb-4">
            <v-slide-group-item
              v-for="(cat, i) in categories"
              :key="i"
              :value="cat"
            >
              <v-chip
                :color="selectedCategory === cat ? 'accent' : 'grey lighten-3'"
                :text-color="selectedCategory === cat ? 'white' : 'black'"
                class="mx-1"
                @click="
                  selectedCategory = cat;
                  selectedIngredient = null;
                "
              >
                {{ cat }}
              </v-chip>
            </v-slide-group-item>
          </v-slide-group>

          <div v-if="isLoadingIngredients" class="d-flex justify-center my-10">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <div v-else>
            <!-- Recherche + bouton -->
            <div class="d-flex align-center my-4" style="gap: 0.5rem;">
              <v-text-field
                v-model="searchQuery"
                placeholder="Rechercher"
                hide-details
                density="compact"
                variant="tonal" 
                prepend-inner-icon="mdi-magnify"
                class="rounded-lg bg-tertiary flex-grow-1"
                rounded="lg"
              />
              
              <v-menu v-model="sortMenuOpen" offset-y>
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    color="accent"
                    variant="tonal"
                    prepend-icon="mdi-sort"
                    rounded="lg"
                  >
                    Trier
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="sortBy = null">
                    <v-list-item-title>
                      <v-icon size="small" class="mr-2">mdi-filter-remove</v-icon>
                      Sans tri
                    </v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item @click="sortBy = 'favorite'">
                    <v-list-item-title>
                      <v-icon size="small" class="mr-2">mdi-star</v-icon>
                      Favoris en premier
                    </v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item @click="sortBy = 'name-asc'">
                    <v-list-item-title>
                      <v-icon size="small" class="mr-2">mdi-sort-alphabetical-ascending</v-icon>
                      Nom (A → Z)
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="sortBy = 'name-desc'">
                    <v-list-item-title>
                      <v-icon size="small" class="mr-2">mdi-sort-alphabetical-descending</v-icon>
                      Nom (Z → A)
                    </v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item @click="sortBy = 'brand-asc'">
                    <v-list-item-title>
                      <v-icon size="small" class="mr-2">mdi-sort-alphabetical-ascending</v-icon>
                      Marque (A → Z)
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="sortBy = 'brand-desc'">
                    <v-list-item-title>
                      <v-icon size="small" class="mr-2">mdi-sort-alphabetical-descending</v-icon>
                      Marque (Z → A)
                    </v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item @click="sortBy = 'category-asc'">
                    <v-list-item-title>
                      <v-icon size="small" class="mr-2">mdi-sort-alphabetical-ascending</v-icon>
                      Catégorie (A → Z)
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="sortBy = 'category-desc'">
                    <v-list-item-title>
                      <v-icon size="small" class="mr-2">mdi-sort-alphabetical-descending</v-icon>
                      Catégorie (Z → A)
                    </v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item @click="sortBy = 'portion-asc'">
                    <v-list-item-title>
                      <v-icon size="small" class="mr-2">mdi-sort-numeric-ascending</v-icon>
                      Portion (croissant)
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="sortBy = 'portion-desc'">
                    <v-list-item-title>
                      <v-icon size="small" class="mr-2">mdi-sort-numeric-descending</v-icon>
                      Portion (décroissant)
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <!-- Tableau -->
            <v-table density="compact" class="bg-tertiary rounded-lg">
              <thead>
                <tr>
                  <th>Ingrédient</th>
                  <th>Nom</th>
                  <th>Catégorie</th>
                  <th>Marque</th>
                  <th>Portion (g)</th>
                  <th class="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in filteredIngredients"
                  :key="item.id"
                  class="ingredients ingredient-row"
                >
                  <td class="ingredient-cell" @click="select(item)">
                    <div class="ingredient-content">
                      <v-icon
                        :color="isItemFavorite(item) ? 'orange' : 'grey lighten-1'"
                        class="mr-2"
                        size="18"
                        @click.stop="toggleFavorite(item)"
                      >
                        mdi-star
                      </v-icon>
                      <v-img
                        v-if="item.productImage"
                        :src="item.productImage"
                        width="32"
                        height="32"
                        fit
                      />
                      <v-icon v-else color="grey lighten-1" size="32"
                        >mdi-food</v-icon
                      >
                    </div>
                  </td>
                  <td @click="select(item)">{{ item.productName }}</td>
                  <td @click="select(item)">{{ item.category }}</td>
                  <td @click="select(item)">{{ item.productBrand ?? "Inconnu" }}</td>
                  <td @click="select(item)">{{ item.servingQuantity }} g</td>
                  <td class="text-center">
                    <v-btn
                      v-if="!item.isEditingDisabled"
                      icon
                      size="small"
                      variant="text"
                      color="error"
                      @click.stop="confirmDelete(item)"
                      class="delete-icon-btn"
                    >
                      <v-icon size="20">mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </div>
      </v-col>
      
      <!-- Edit modal component -->
      <EditIngredientModal v-model="editModalOpen" :ingredient="selectedIngredient" @saved="onIngredientSaved" />

      <!-- Fiche ingrédient -->
      <v-col cols="12" md="3" class="bg-secondary pa-4 rounded-xl">
        <div v-if="selectedIngredient">
          <div class="d-flex align-start justify-space-between mb-4">
            <!-- Nom du produit -->
            <h1 class="font-weight-bold text-h5 mb-0">
              {{ selectedIngredient.productName }}
            </h1>

            <v-img
              :src="selectedIngredient.productImage"
              width="100"
              max-height="100"
              class="ml-4 rounded"
              contain
              v-if="selectedIngredient.productImage"
            />
            <v-icon v-else color="grey lighten-1" size="100" class="ml-4"
              >mdi-food-apple-outline</v-icon
            >
          </div>

          <div v-if="selectedIngredient.nutriments_map">
            <h4 class="mt-4">Nutriments</h4>
            <ul class="nutriment-list">
              <p
                v-for="(item, index) in selectedIngredient.nutriments_map"
                :key="index"
                class="nutriment-line"
              >
                <span>{{ $t(item.key) }}</span>
                <span>{{ item.value }}</span>
              </p>
            </ul>
          </div>

          <div
            v-if="
              selectedIngredient.allergens &&
              selectedIngredient.allergens.length
            "
          >
            <p class="mb-2 font-weight-medium">Allergènes :</p>
            <div class="allergen-list">
              <v-chip
                v-for="(allergen, index) in selectedIngredient.allergens"
                :key="index"
                color="blue lighten-2"
                text-color="white"
                class="ma-1"
                label
                small
              >
                {{ $t(allergen) }}
              </v-chip>
            </div>
          </div>
          <div v-else>
            <p class="text-grey">Aucun allergène</p>
          </div>

          <p class="text-red mt-2" v-if="selectedIngredient.novaGroup?.string">
            <v-chip :color="selectedIngredient.novaGroup.color">
              {{ $t(selectedIngredient.novaGroup.string) }}
            </v-chip>
          </p>
          <div class="ingredientActions">
            <div class="ingredientActions__title">Actions</div>
            <v-btn
              block
              class="ingredientActionBtn"
              rounded="lg"
              elevation="0"
              color="accent"
              variant="tonal"
              @click="editModalOpen = true"
            >
              <v-icon start size="18">mdi-pencil</v-icon>
              Modifier l'ingrédient
            </v-btn>
            <v-btn
              v-if="!selectedIngredient.isEditingDisabled"
              block
              color="error"
              class="ingredientActionBtn"
              rounded="lg"
              elevation="0"
              variant="tonal"
              @click="confirmDelete()"
            >
              <v-icon start size="18">mdi-delete</v-icon>
              Supprimer
            </v-btn>

            <v-btn
              block
              class="ingredientActionBtn"
              :color="isItemFavorite(selectedIngredient) ? 'orange' : 'purple'"
              rounded="lg"
              variant="tonal"
              @click="toggleFavorite(selectedIngredient)"
            >
              <v-icon start size="18">
                {{ isItemFavorite(selectedIngredient) ? 'mdi-star' : 'mdi-star-outline' }}
              </v-icon>
              {{
                isItemFavorite(selectedIngredient)
                  ? "Retirer des favoris"
                  : "Mettre en favoris"
              }}
            </v-btn>
          </div>

        </div>
      </v-col>
    </v-row>

    <!-- Dialog de confirmation de suppression -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5 font-weight-bold">
          ⚠️ Confirmer la suppression
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            Êtes-vous sûr de vouloir supprimer <strong>{{ ingredientToDelete?.productName }}</strong> ?
          </p>
          <v-alert type="warning" variant="tonal" class="mb-0">
            <strong>Attention :</strong> Toutes les recettes contenant cet ingrédient seront également supprimées.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="deleteDialog = false"
          >
            Annuler
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="deleteSelectedIngredient"
          >
            Supprimer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" top right>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<style scoped>
.addIngredientBanner {
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-accent)),
    rgba(255, 255, 255, 0.6)
  );
  border-radius: 8px;
  color: white;
  margin-bottom: 1.5rem;
}

.headerWithAction {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.nutriment-line {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.ingredients:hover {
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.05);
}

.ingredient-row {
  min-height: 56px;
  height: 56px;
}

.ingredient-cell {
  vertical-align: middle !important;
  height: 56px;
}

.ingredient-content {
  display: flex;
  align-items: center;
  height: 100%;
}

.delete-icon-btn {
  opacity: 0.7;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.delete-icon-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}

.ingredientActions {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid rgba(var(--v-theme-text), 0.08);
}

.ingredientActions__title {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-text), 0.6);
}

.ingredientActionBtn {
  text-transform: none;
  font-weight: 600;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.14);
}

.rowCustom {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

.browseBaseRecipesBanner {
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-accent)),
    rgba(255, 255, 255, 0.6)
  );
  color: white;
  margin-bottom: 1.5rem;
}
</style>
